/**
 * Balance Journey Page
 * Shows day-by-day progress for a specific emotional reset journey.
 * Features vertical unlock path similar to Teen Challenge.
 */

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, 
  Heart, 
  Play,
  Check,
  Lock,
  Sparkles,
  Moon,
  Sun,
  Pencil
} from "lucide-react";
import axios from "axios";
import JourneyIntroScreen from "./JourneyIntroScreen";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BalanceJourneyPage = () => {
  const navigate = useNavigate();
  const { journeyId } = useParams();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIntro, setShowIntro] = useState(false);
  const [startingJourney, setStartingJourney] = useState(false);
  
  const getToken = () => localStorage.getItem("reclaim_token");

  useEffect(() => {
    loadJourney();
  }, [journeyId]);

  const loadJourney = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/balance/journey/${journeyId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setJourney(response.data);
      
      // Show intro if not started
      if (!response.data.progress?.started) {
        setShowIntro(true);
      }
    } catch (err) {
      console.error("Error loading journey:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartJourney = async () => {
    setStartingJourney(true);
    try {
      await axios.post(
        `${API_URL}/api/balance/journey/${journeyId}/start`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      await loadJourney();
    } catch (err) {
      console.error("Error starting journey:", err);
    } finally {
      setStartingJourney(false);
    }
  };

  const handleIntroComplete = () => {
    setShowIntro(false);
    if (!journey?.progress?.started) {
      handleStartJourney();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Heart size={40} className="text-violet-400" />
        </motion.div>
      </div>
    );
  }

  if (!journey) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-white/60 mb-4">Journey not found</p>
          <button
            onClick={() => navigate("/balance")}
            className="text-violet-400 hover:text-violet-300"
          >
            Return to Balance Hub
          </button>
        </div>
      </div>
    );
  }

  // Not started - show entry screen
  if (!journey.progress?.started && !showIntro) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col">
        {/* Header */}
        <div className="p-6">
          <button
            onClick={() => navigate("/balance")}
            className="text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
        </div>

        {/* Entry Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring" }}
            className="w-24 h-24 rounded-3xl mb-8 flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${journey.gradient?.[0]}, ${journey.gradient?.[1]})`
            }}
          >
            <Heart size={44} className="text-white" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-2xl font-light text-white mb-2"
          >
            {journey.title}
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/50 mb-8 max-w-sm"
          >
            {journey.subtitle}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-white/40 text-sm mb-8 max-w-xs leading-relaxed italic"
          >
            "{journey.intro_text}"
          </motion.p>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex gap-8 mb-10"
          >
            <div className="text-center">
              <div 
                className="text-3xl font-light"
                style={{ color: journey.gradient?.[0] }}
              >
                {journey.duration_days}
              </div>
              <div className="text-white/40 text-xs uppercase tracking-wider">Days</div>
            </div>
            <div className="text-center">
              <div 
                className="text-3xl font-light"
                style={{ color: journey.gradient?.[1] }}
              >
                6
              </div>
              <div className="text-white/40 text-xs uppercase tracking-wider">Sessions/Day</div>
            </div>
          </motion.div>

          {/* Start Button */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowIntro(true)}
            disabled={startingJourney}
            className="px-10 py-4 rounded-2xl text-white font-medium shadow-xl flex items-center gap-3"
            style={{
              background: `linear-gradient(135deg, ${journey.gradient?.[0]}, ${journey.gradient?.[1]})`
            }}
            data-testid="start-balance-journey"
          >
            <Play size={20} />
            <span>Begin Journey</span>
          </motion.button>
        </div>

        {/* Bottom text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="p-6 text-center"
        >
          <p className="text-white/30 text-xs">
            This is not therapy. This is a gentle return to balance.
          </p>
        </motion.div>
      </div>
    );
  }

  // Show intro screen
  if (showIntro) {
    return (
      <JourneyIntroScreen
        onComplete={handleIntroComplete}
        journeyTitle={journey.title}
        gradient={journey.gradient}
        duration={3500}
      />
    );
  }

  // Main journey view with days
  const currentDay = journey.progress?.current_day || 1;
  const completedDays = journey.progress?.completed_days || [];

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <div className="p-6 flex items-center justify-between">
        <button
          onClick={() => navigate("/balance")}
          className="text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft size={24} />
        </button>
        
        {/* Progress indicator */}
        <div className="flex items-center gap-2">
          <span className="text-white/40 text-sm">
            Day {currentDay} of {journey.duration_days}
          </span>
        </div>
      </div>

      {/* Journey Title */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-6 pb-6 text-center"
      >
        <h1 className="text-xl font-light text-white mb-1">{journey.title}</h1>
        <p className="text-white/40 text-sm">Your path back to balance</p>
      </motion.div>

      {/* Days List */}
      <div className="px-6 pb-12">
        <div className="relative">
          {/* Vertical line connecting days */}
          <div 
            className="absolute left-7 top-8 bottom-8 w-0.5 bg-white/5"
            style={{
              background: `linear-gradient(180deg, ${journey.gradient?.[0]}20, ${journey.gradient?.[1]}20)`
            }}
          />

          {journey.days?.map((day, index) => {
            const isCompleted = completedDays.includes(day.day);
            const isCurrent = day.day === currentDay;
            const isLocked = day.day > currentDay;
            const actionCompleted = journey.progress?.action_completions?.[day.day];

            return (
              <motion.div
                key={day.day}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + index * 0.1 }}
                className="relative mb-4"
              >
                {/* Day card */}
                <button
                  onClick={() => !isLocked && navigate(`/balance/${journeyId}/day/${day.day}`)}
                  disabled={isLocked}
                  className={`w-full p-4 pl-16 rounded-xl text-left transition-all duration-300 ${
                    isLocked 
                      ? "opacity-50 cursor-not-allowed" 
                      : "hover:bg-white/5 active:scale-[0.98]"
                  }`}
                  style={{
                    background: isCurrent 
                      ? `linear-gradient(135deg, ${journey.gradient?.[0]}15, ${journey.gradient?.[1]}15)`
                      : "transparent"
                  }}
                  data-testid={`balance-day-${day.day}`}
                >
                  {/* Day number circle */}
                  <div 
                    className={`absolute left-4 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                      isCompleted 
                        ? "bg-emerald-500 text-white"
                        : isCurrent
                          ? "text-white"
                          : "bg-white/5 text-white/40"
                    }`}
                    style={isCurrent && !isCompleted ? {
                      background: `linear-gradient(135deg, ${journey.gradient?.[0]}, ${journey.gradient?.[1]})`
                    } : {}}
                  >
                    {isCompleted ? (
                      <Check size={14} />
                    ) : isLocked ? (
                      <Lock size={12} />
                    ) : (
                      day.day
                    )}
                  </div>

                  {/* Content */}
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className={`font-medium ${isLocked ? "text-white/40" : "text-white"}`}>
                        Day {day.day}: {day.title}
                      </h3>
                      {day.has_interactive && !isLocked && (
                        <Pencil size={12} className="text-violet-400" />
                      )}
                    </div>
                    <p className={`text-sm ${isLocked ? "text-white/20" : "text-white/50"}`}>
                      {day.theme}
                    </p>
                    
                    {/* Session indicators for current/completed days */}
                    {!isLocked && (
                      <div className="flex gap-2 mt-2">
                        <div className="flex items-center gap-1 text-xs text-white/30">
                          <Sun size={10} />
                          <span>Immersion</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-white/30">
                          <Moon size={10} />
                          <span>Night Audio</span>
                        </div>
                        {actionCompleted && (
                          <div className="flex items-center gap-1 text-xs text-emerald-400">
                            <Check size={10} />
                            <span>Action Done</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Floating Midday Reset Button */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        onClick={() => navigate(`/balance/${journeyId}/midday-reset`)}
        className="fixed bottom-6 right-6 px-5 py-3 rounded-full shadow-lg flex items-center gap-2"
        style={{
          background: `linear-gradient(135deg, ${journey.gradient?.[0]}, ${journey.gradient?.[1]})`
        }}
        data-testid="midday-reset-button"
      >
        <Sparkles size={18} className="text-white" />
        <span className="text-white text-sm font-medium">Return to Balance</span>
      </motion.button>
    </div>
  );
};

export default BalanceJourneyPage;
